import MaskedTextField from '@/common/components/MaskedTextField'
import { Calendar } from '@/common/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/components/ui/popover'
import { replacePeriodToUnderscore } from '@/common/utils/transformers'
import { endOfDay, format, parse, startOfDay } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Control, Controller, FieldErrors, Path } from 'react-hook-form'

export interface DateInputProps<T extends {}> {
  fieldName: Path<T>
  control: Control<T>
  errors?: FieldErrors<T>
  label?: string
  markAsRequired?: boolean
  className?: string
  disabled?: boolean
  mask?: string
  placeholder?: string
  errorMessage?: string
  minDate?: Date
  maxDate?: Date
  handleChange?: (value: string) => void;
  value?: string;
  showCalendar?: boolean;
}

export const DateInput = <T extends {}>({
  fieldName,
  control,
  errors,
  label,
  markAsRequired = false,
  className,
  disabled = false,
  mask = '00/00/0000',
  placeholder = 'DD/MM/AAAA',
  errorMessage,
  minDate,
  maxDate,
  handleChange,
  value,
  showCalendar = true,
}: DateInputProps<T>) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>()
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const normalizedMinDate = minDate ? startOfDay(minDate) : undefined
  const normalizedMaxDate = maxDate ? endOfDay(maxDate) : undefined

  useEffect(() => {
    if (value) {
      setSelectedDate(parse(value, 'dd/MM/yyyy', new Date()));
    }
  }, [value]);

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => (
        <MaskedTextField
          LeftIcon={
            showCalendar ? (
              <Popover
                open={isPopoverOpen}
                onOpenChange={(open) => {
                  setIsPopoverOpen(open)
                }}
              >
                <PopoverTrigger
                  data-testid={`${replacePeriodToUnderscore(fieldName)}_calendar_trigger`}
                  asChild
                  onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                  disabled={disabled}
                >
                  <button
                    type="button"
                    className="flex items-center justify-center w-6 h-6 cursor-pointer group disabled:cursor-default"
                    disabled={disabled}
                  >
                    <CalendarIcon
                      strokeWidth={1}
                      className="w-4 h-4 text-gray-400 hover:text-gray-500 group-disabled:hover:text-gray-400"
                    />
                  </button>
                </PopoverTrigger>

                <PopoverContent className="w-auto p-0 bg-[#212023] border-neutral-op-8 dark" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (!date) {
                        field.onChange('')
                        setSelectedDate(undefined)
                        handleChange?.('');
                        return
                      }

                      setSelectedDate(date)

                      field.onChange(format(date, 'dd/MM/yyyy'))
                      handleChange?.(format(date, 'dd/MM/yyyy'));

                      setIsPopoverOpen(false)
                    }}
                    disabled={(date) => {
                      const current = startOfDay(date)

                      if (normalizedMinDate && current < normalizedMinDate) return true
                      if (normalizedMaxDate && current > normalizedMaxDate) return true

                      return false
                    }}
                    className="w-auto"
                  />
                </PopoverContent>
              </Popover>
            ) : undefined
          }
          label={label}
          markAsRequired={markAsRequired}
          mask={mask}
          placeholder={placeholder}
          className={className}
          errorMessage={errorMessage || (errors?.[fieldName as keyof typeof errors]?.message as string | undefined)}
          name={fieldName}
          value={value || field.value || ''}
          onChange={(e) => {
            field.onChange(e.target.value)
            handleChange?.(e.target.value)

            setIsPopoverOpen(false)
          }}
          disabled={disabled}
          onFocus={() => showCalendar && setIsPopoverOpen(true)}
        />
      )}
    />
  )
}
